import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Search, Clock, RefreshCw } from "lucide-react";
import ArthaTextField from "../../fields/ArthaTextField";
import {
  fetchStudentPresenceSessionsAPI,
  fetchStudentSightingsAPI,
} from "../../actioncreators/actioncreators";
import "./dashboard.css";

const ACCENT = "#fd610a";

// ArthaTRACK stores session_start/session_end as raw derived timestamps -
// it does not compute or store an attendance status (present/absent/late).
// This page only ever displays the raw time range and duration; see
// Backend/helpers/studentAttendance.go's FetchStudentPresenceSessions doc
// comment for why that's a deliberate design boundary, not a gap here.
const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleTimeString(undefined, { timeStyle: "short" });
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { dateStyle: "medium" });
};

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 1) return "< 1 min";
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs === 0) return `${mins} min`;
  return `${hrs}h ${mins}m`;
};

// Groups raw sessions (each a short continuous visit, often several per
// student per room per day since a session breaks on any gap over the
// tolerance in CronJobs/helpers/sessionDeriver.go) into one row per
// student + beacon/room + calendar day, with total minutes summed and the
// earliest/latest timestamps kept as first-seen/last-seen for that day.
const summarizeByStudentBeaconDay = (sessions) => {
  const groups = new Map();
  for (const s of sessions) {
    const start = s.sessionStart ? new Date(s.sessionStart) : null;
    const dayKey = start && !Number.isNaN(start.getTime())
      ? start.toDateString()
      : "unknown";
    const key = `${s.userId}|${s.beaconId}|${dayKey}`;

    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        key,
        studentName: s.studentName,
        userId: s.userId,
        beaconName: s.beaconName,
        beaconId: s.beaconId,
        locationName: s.locationName,
        day: s.sessionStart,
        firstSeen: s.sessionStart,
        lastSeen: s.sessionEnd,
        totalMinutes: s.durationMinutes || 0,
        sessionCount: 1,
      });
    } else {
      existing.totalMinutes += s.durationMinutes || 0;
      existing.sessionCount += 1;
      if (s.sessionStart && new Date(s.sessionStart) < new Date(existing.firstSeen)) {
        existing.firstSeen = s.sessionStart;
      }
      if (s.sessionEnd && new Date(s.sessionEnd) > new Date(existing.lastSeen)) {
        existing.lastSeen = s.sessionEnd;
      }
    }
  }
  return Array.from(groups.values()).sort(
    (a, b) => new Date(b.day) - new Date(a.day)
  );
};

// What became of a raw sighting - see Backend/helpers/studentAttendance.go's
// FetchStudentSightings.
const SIGHTING_STATUS = {
  PENDING: { label: "Pending", color: "#b45309", bg: "#fef3c7", hint: "Not processed yet, or the visit is still under the minimum time" },
  IN_SESSION: { label: "In a session", color: "#166534", bg: "#dcfce7", hint: "Counted in a presence session" },
  DISCARDED: { label: "Discarded", color: "#6b7280", bg: "#f3f4f6", hint: "The visit was shorter than the minimum, so no session was created" },
};

const AUTO_REFRESH_MS = 60 * 1000;

const StudentPresence = () => {
  const [sessions, setSessions] = useState([]);
  const [sightings, setSightings] = useState([]);
  const [minSessionMinutes, setMinSessionMinutes] = useState(2);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState("summary"); // "summary" | "detail" | "sightings"

  useEffect(() => {
    loadAll();
    // Sessions are written by a cron job every couple of minutes, so keep the
    // page current without the user having to reload it.
    const timer = setInterval(() => loadAll(true), AUTO_REFRESH_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async (silent = false) => {
    await Promise.all([loadSessions(silent), loadSightings()]);
  };

  const loadSightings = async () => {
    try {
      const response = await fetchStudentSightingsAPI({});
      if (response?.businessStatusCode === 2) {
        setSightings(response.sightings || []);
        if (response.minSessionMinutes) setMinSessionMinutes(response.minSessionMinutes);
      } else {
        setSightings([]);
      }
    } catch (error) {
      console.error("Failed to load student sightings:", error);
      setSightings([]);
    }
  };

  const loadSessions = async (silent = false) => {
    if (silent) {
      // Background refresh: update in place, no loading flash, no error toast.
      try {
        const response = await fetchStudentPresenceSessionsAPI({});
        if (response?.businessStatusCode === 2) setSessions(response.sessions || []);
      } catch (error) {
        console.error("Background refresh of presence sessions failed:", error);
      }
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetchStudentPresenceSessionsAPI({});
      if (response?.businessStatusCode === 2) {
        setSessions(response.sessions || []);
      } else {
        setSessions([]);
        if (response?.message) toast.error(response.message);
      }
    } catch (error) {
      console.error("Failed to load student presence sessions:", error);
      toast.error("Failed to load presence sessions.");
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSessions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sessions;
    return sessions.filter((s) => {
      const student = (s.studentName || "").toLowerCase();
      const beacon = (s.beaconName || "").toLowerCase();
      const location = (s.locationName || "").toLowerCase();
      return student.includes(term) || beacon.includes(term) || location.includes(term);
    });
  }, [sessions, searchTerm]);

  const summaryRows = useMemo(
    () => summarizeByStudentBeaconDay(filteredSessions),
    [filteredSessions]
  );

  const filteredSightings = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sightings;
    return sightings.filter((s) => {
      const student = (s.studentName || "").toLowerCase();
      const beacon = (s.beaconName || "").toLowerCase();
      const location = (s.locationName || "").toLowerCase();
      return student.includes(term) || beacon.includes(term) || location.includes(term);
    });
  }, [sightings, searchTerm]);

  return (
    <div className="container-fluid" style={{ padding: "20px" }}>
      <div className="d-flex align-items-center" style={{ gap: "10px", marginBottom: "16px" }}>
        <Clock size={22} color={ACCENT} />
        <h4 style={{ margin: 0, fontWeight: 600 }}>User Presence</h4>
      </div>
      <div style={{ color: "#757575", fontSize: 13, marginBottom: "16px", maxWidth: 720 }}>
        Raw arrival/departure times derived from beacon sightings. ArthaTRACK
        does not decide attendance status here; this is the same raw data an
        external ERP would pull to match against its own timetable.
      </div>
      <div
        style={{
          background: "#fff7ed",
          border: "1px solid #fed7aa",
          color: "#9a3412",
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 13,
          marginBottom: "16px",
          maxWidth: 720,
        }}
      >
        A presence session appears after a user stays near a beacon for at
        least <strong>{minSessionMinutes} minutes</strong>, and is created within a couple of
        minutes after that. Shorter visits are not recorded as sessions; use{" "}
        <strong>Raw sightings</strong> to see exactly what each phone reported
        and what happened to it.
      </div>

      <div className="d-flex align-items-center flex-wrap" style={{ gap: "16px", marginBottom: "16px" }}>
        <div style={{ maxWidth: 360, flex: 1, minWidth: 260 }}>
          <ArthaTextField
            fullWidth
            placeholder="Search by user, beacon, or location"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search size={16} style={{ marginRight: 8, color: "#999" }} />,
            }}
          />
        </div>
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(e, next) => next && setView(next)}
        >
          <ToggleButton value="summary" sx={{ textTransform: "none", px: 2 }}>
            Total time per day
          </ToggleButton>
          <ToggleButton value="detail" sx={{ textTransform: "none", px: 2 }}>
            Individual visits
          </ToggleButton>
          <ToggleButton value="sightings" sx={{ textTransform: "none", px: 2 }}>
            Raw sightings
          </ToggleButton>
        </ToggleButtonGroup>
        <button
          type="button"
          onClick={() => loadAll()}
          title="Refresh"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            border: "1px solid #d0d0d0",
            background: "#fff",
            borderRadius: 6,
            padding: "6px 12px",
            fontSize: 13,
            cursor: "pointer",
            color: "#374151",
          }}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      <TableContainer component={Paper} elevation={1}>
        {view === "summary" ? (
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: "#fafafa" }}>
                <TableCell style={{ fontWeight: 600 }}>User</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Beacon / Location</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell style={{ fontWeight: 600 }}>First Seen</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Last Seen</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Total Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: "32px" }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : summaryRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: "32px", color: "#999" }}>
                    No presence sessions recorded yet.
                  </TableCell>
                </TableRow>
              ) : (
                summaryRows.map((r) => (
                  <TableRow key={r.key} hover>
                    <TableCell>{r.studentName || r.userId}</TableCell>
                    <TableCell>
                      <div>{r.beaconName || r.beaconId}</div>
                      {r.locationName && (
                        <Chip
                          size="small"
                          label={r.locationName}
                          sx={{ mt: 0.5, fontSize: "11px", height: "20px" }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{formatDate(r.day)}</TableCell>
                    <TableCell>{formatTime(r.firstSeen)}</TableCell>
                    <TableCell>{formatTime(r.lastSeen)}</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>
                      {formatDuration(r.totalMinutes)}
                      {r.sessionCount > 1 && (
                        <span style={{ color: "#999", fontWeight: 400, fontSize: "12px" }}>
                          {" "}
                          ({r.sessionCount} visits)
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        ) : view === "sightings" ? (
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: "#fafafa" }}>
                <TableCell style={{ fontWeight: 600 }}>User</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Beacon / Location</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Seen at</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Signal (RSSI)</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" style={{ padding: "32px" }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredSightings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" style={{ padding: "32px", color: "#999" }}>
                    No sightings received in the last 24 hours. A phone only uploads sightings
                    while the user is clocked in, near a registered beacon, and above its signal
                    threshold.
                  </TableCell>
                </TableRow>
              ) : (
                filteredSightings.map((s) => {
                  const st = SIGHTING_STATUS[s.status] || SIGHTING_STATUS.DISCARDED;
                  return (
                    <TableRow key={s.sightingId} hover>
                      <TableCell>{s.studentName || s.userId}</TableCell>
                      <TableCell>
                        <div>{s.beaconName || s.beaconId}</div>
                        {s.locationName && (
                          <Chip
                            size="small"
                            label={s.locationName}
                            sx={{ mt: 0.5, fontSize: "11px", height: "20px" }}
                          />
                        )}
                      </TableCell>
                      <TableCell>{formatDateTime(s.seenAt)}</TableCell>
                      <TableCell>{s.rssi} dBm</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={st.label}
                          title={st.hint}
                          sx={{ fontSize: "11px", height: "22px", color: st.color, backgroundColor: st.bg, fontWeight: 600 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: "#fafafa" }}>
                <TableCell style={{ fontWeight: 600 }}>User</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Beacon / Location</TableCell>
                <TableCell style={{ fontWeight: 600 }}>From</TableCell>
                <TableCell style={{ fontWeight: 600 }}>To</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" style={{ padding: "32px" }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredSessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" style={{ padding: "32px", color: "#999" }}>
                    No presence sessions recorded yet.
                  </TableCell>
                </TableRow>
              ) : (
                filteredSessions.map((s) => (
                  <TableRow key={s.studentPresenceSessionId} hover>
                    <TableCell>{s.studentName || s.userId}</TableCell>
                    <TableCell>
                      <div>{s.beaconName || s.beaconId}</div>
                      {s.locationName && (
                        <Chip
                          size="small"
                          label={s.locationName}
                          sx={{ mt: 0.5, fontSize: "11px", height: "20px" }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{formatDateTime(s.sessionStart)}</TableCell>
                    <TableCell>{formatDateTime(s.sessionEnd)}</TableCell>
                    <TableCell>{formatDuration(s.durationMinutes)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </div>
  );
};

export default StudentPresence;
